import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Sparkles, X, CheckCircle2 } from 'lucide-react';

interface WelcomeToastProps {
  show: boolean;
  userName: string;
  userPhoto?: string | null;
  onClose: () => void;
}

export default function WelcomeToast({ show, userName, userPhoto, onClose }: WelcomeToastProps) {
  // Auto-close after 2 seconds
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: -50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 350, damping: 25 }}
          className="fixed top-20 left-1/2 -translate-x-1/2 md:left-auto md:-translate-x-0 md:right-6 md:top-20 z-[9999] w-[92%] max-w-sm"
        >
          <div className="bg-white/95 backdrop-blur-md border border-emerald-500/20 shadow-[0_20px_50px_rgba(42,84,49,0.15)] rounded-2xl p-4 flex items-center gap-4.5 relative overflow-hidden">
            {/* Elegant visual highlight gradient */}
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 via-transparent to-amber-500/5 pointer-events-none" />
            
            {/* Left side: Avatar or Sparkle Indicator */}
            <div className="relative shrink-0">
              {userPhoto ? (
                <div className="relative w-12 h-12 rounded-full border-2 border-emerald-500/30 overflow-hidden shadow-sm">
                  <img 
                    src={userPhoto} 
                    alt={userName} 
                    referrerPolicy="no-referrer"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute -bottom-1 -right-1 bg-emerald-500 text-white rounded-full p-0.5 border border-white">
                    <CheckCircle2 className="w-3.5 h-3.5 fill-emerald-500 text-white" />
                  </div>
                </div>
              ) : (
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center border border-emerald-100 shadow-xs">
                  <Sparkles className="w-6 h-6 text-emerald-600 animate-pulse" />
                </div>
              )}
            </div>

            {/* Middle: Welcome message */}
            <div className="flex-1 min-w-0 pr-2">
              <h4 className="text-sm font-black text-slate-800 tracking-tight leading-snug flex items-center gap-1">
                <span>Welcome back, {userName}! 👋</span>
              </h4>
              <p className="text-[11px] text-slate-500 font-medium leading-normal mt-0.5">
                लॉगिन सफल रहा! आपका स्वागत है। ✨
              </p>
            </div>

            {/* Right side: Manual close button */}
            <button
              onClick={onClose}
              className="absolute top-2.5 right-2.5 p-1 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all cursor-pointer active:scale-90"
              aria-label="Close notification"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
